import { create } from "zustand";
import { NOTIFICATION_STATUS } from "../pages/utils/notificationUtils";

export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  cursor: null,
  
  pendingOperations: new Set(),

  fetchUnreadCount: async ({ fetchData, appProperties }) => {

    if (!appProperties) return;

    try {
      const response = await fetchData(`notifications/count/unread`, "GET", null, appProperties);
      const resp = JSON.parse(response);
      const count = Number(resp?.data?.data) || 0;
      set({ unreadCount: count });
    } catch (error) {
      console.error("Error fetching unread count:", error);
      set({ unreadCount: 0 });
    } finally {
      set({ loading: false });
    }
  },

  fetchNotifications: async ({ fetchDataByPagination, appProperties, shouldRefresh = false }) => {

    const { loading, cursor, notifications } = get();
    if (loading) return;

    if (shouldRefresh) {
      set({ notifications: [], cursor: null });
    }

    set({ loading: true });
    try {
      const currentCursor = shouldRefresh ? null : cursor;

      const response = await fetchDataByPagination(
        `notifications`,
        "GET",
        null,
        appProperties,
        null,
        25,
        currentCursor,
        null,
        null
      );

      const notificationsData = response?.data;
      if (!Array.isArray(notificationsData)) {
        set({ cursor: null, notifications: shouldRefresh ? [] : notifications });
        return;
      }

      let nextCursor = null;
      if (response?.paginationModel) {
        if (response.paginationModel.cursor) {
          nextCursor = { cursor: response.paginationModel.cursor };
        } else if (response.paginationModel.lastKey) {
          nextCursor = {
            lastKey: response.paginationModel.lastKey,
            lastRange: response.paginationModel.lastRange || null,
          };
        }
      }

      set((state) => ({
        notifications: shouldRefresh ? notificationsData : [...state.notifications, ...notificationsData],
        cursor: nextCursor
      }));
    } catch (err) {
      console.error("Error fetching notifications:", err);
      if (shouldRefresh) set({ notifications: [] });
    } finally {
      set({ loading: false });
    }
  },

  onIncomingNotification: (notification, { addToList = false, incrementUnreadCount = true } = {}) => {
    if (!notification) return;
    
    const notificationId = notification?.notificationId;
    if (!notificationId) return;
    
    set((state) => {
      const existingNotifications = state.notifications || [];
      
      // Check if notification already exists
      const existingIndex = existingNotifications.findIndex(n => n.notificationId === notificationId);
      const notificationExists = existingIndex !== -1;
      
      let updatedNotifications;
      if (addToList) {
        if (notificationExists) {
          // Update existing notification in place
          updatedNotifications = [...existingNotifications];
          updatedNotifications[existingIndex] = notification;
        } else {
          // Add new notification at the beginning
          updatedNotifications = [notification, ...existingNotifications];
        }
      } else {

        if (notificationExists) {
          updatedNotifications = [...existingNotifications];
          updatedNotifications[existingIndex] = notification;
        } else {
          updatedNotifications = existingNotifications;
        }
      }
      
      return {
        unreadCount: incrementUnreadCount ? Math.max(0, (state?.unreadCount || 0) + 1) : (state?.unreadCount || 0),
        notifications: updatedNotifications,
      };
    });
  },

  markAsRead: async ({ fetchData, appProperties, notification }) => {
    if (!notification?.notificationId) return;
    
    const notificationId = notification.notificationId;
    
    const operationKey = `markAsRead-${notificationId}`;
    const { pendingOperations } = get();
    if (pendingOperations.has(operationKey)) {
      return;
    }
    
    if (notification.status === NOTIFICATION_STATUS.READ.value) {
      return;
    }
    
    set((state) => ({
      notifications: (state.notifications || []).map((n) =>
        n.notificationId === notificationId ? { ...n, status: NOTIFICATION_STATUS.READ.value } : n
      ),
      unreadCount: Math.max(0, (state.unreadCount || 0) - 1),
      pendingOperations: new Set([...state.pendingOperations, operationKey]),
    }));
    
    try {
      await fetchData(`notifications/${notificationId}/read`, "PUT", null, appProperties);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      set((state) => ({
        notifications: (state.notifications || []).map((n) =>
          n.notificationId === notificationId ? { ...n, status: NOTIFICATION_STATUS.UNREAD.value } : n
        ),
        unreadCount: Math.max(0, (state.unreadCount || 0) + 1),
      }));
    } finally {
      set((state) => {
        const newOps = new Set(state.pendingOperations);
        if (newOps.has(operationKey)) {
          newOps.delete(operationKey);
        }
        return { pendingOperations: newOps };
      });
    }
  },


  markNotificationsAsReadByIds: ({ notificationIds }) => {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0) return;

    set((state) => {
      const currentNotifications = state.notifications || [];
      const idSet = new Set(notificationIds.filter(Boolean));
      let updatedCount = 0;
      const updatedNotifications = currentNotifications.map((n) => {
        if (!idSet.has(n.notificationId)) return n;
        if (n.status === NOTIFICATION_STATUS.READ.value) return n;
        updatedCount += 1;
        return { ...n, status: NOTIFICATION_STATUS.READ.value };
      });
      return {
        notifications: updatedNotifications,
        unreadCount: Math.max(0, (state.unreadCount || 0) - updatedCount),
      };
    });
  },

  markAllAsRead: async ({ fetchData, appProperties }) => {
    
    const originalState = {
      notifications: get().notifications,
      unreadCount: get().unreadCount,
    };
    
    set((state) => ({
      notifications: (state.notifications || []).map((n) => ({ ...n, status: NOTIFICATION_STATUS.READ.value })),
      unreadCount: 0,
    }));
    
    try {
      await fetchData(`notifications/read-all`, "PUT", null, appProperties);
    } catch (error) {
      console.error("Error marking all as read:", error);
      set({
        notifications: originalState.notifications,
        unreadCount: originalState.unreadCount,
      });
    } finally {
      set({ loading: false });
    }
  },

  clearStore: () => {
    set({
      notifications: [],
      unreadCount: 0,
      loading: false,
      cursor: null,
      pendingContactInfo: null,
      pendingOperations: new Set(),
    });
  },  
}));